import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://www.kalia-tech.com';
const DEFAULT_IMAGE = `${SITE_URL}/kalia-favicon.png`;

function upsertMeta(attrName, attrValue, content) {
    let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function upsertLink(rel, href, hreflang) {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
    let el = document.getElementById(id);
    if (data == null) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

// Applies per-page meta tags directly to <head>. Updates the tags shipped in
// public/index.html in place, so there are never duplicates. `jsonLd` adds a
// page-specific structured-data script, removed when absent.
function Seo({ path = '/', titleKey, descriptionKey, title: rawTitle, description: rawDescription, image = DEFAULT_IMAGE, type = 'website', jsonLd = null }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
    const title = rawTitle || t(titleKey);
    const description = rawDescription || t(descriptionKey);
    const canonical = `${SITE_URL}${path}`;
    const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;

    useEffect(() => {
        document.title = title;
        document.documentElement.lang = lang;

        upsertMeta('name', 'description', description);
        upsertLink('canonical', canonical);

        upsertLink('alternate', canonical, 'fr');
        upsertLink('alternate', canonical, 'en');
        upsertLink('alternate', canonical, 'x-default');

        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:site_name', 'Kalia');
        upsertMeta('property', 'og:title', title);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:url', canonical);
        upsertMeta('property', 'og:image', image);

        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', title);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', image);

        upsertJsonLd('seo-jsonld', jsonLdString ? JSON.parse(jsonLdString) : null);
    }, [title, description, canonical, image, type, lang, jsonLdString]);

    return null;
}

export default Seo;
